import { useCallback, useEffect, useState } from 'react';
import {
  View, Text, StyleSheet, ScrollView, RefreshControl, Switch, TouchableOpacity, Alert, ActivityIndicator, Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import { api } from '../../services/api';
import { Button } from '../../components/Button';
import ImageLightbox from '../../components/ImageLightbox';
import { RD, fonts } from '../../theme/colors';

const fmt = (cents: any) => {
  const n = (Number(cents) || 0) / 100;
  return '$' + (Number.isInteger(n) ? String(n) : n.toFixed(2));
};

export function StorageScreen({ navigation }: any) {
  const [available, setAvailable] = useState<any[]>([]);
  const [mine, setMine] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lightbox, setLightbox] = useState<{ imgs: string[]; i: number } | null>(null);
  const [accepts, setAccepts] = useState<Record<string, { pickup: boolean; monthly: boolean }>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [a, m] = await Promise.all([
        api.get('/storage/driver/available').then(r => r.data).catch(() => []),
        api.get('/storage/driver/mine').then(r => r.data).catch(() => []),
      ]);
      setAvailable(Array.isArray(a) ? a : []);
      setMine(Array.isArray(m) ? m : []);
    } finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useFocusEffect(useCallback(() => { load(); }, [load]));

  const toggle = (id: string, key: 'pickup' | 'monthly', val: boolean) =>
    setAccepts(prev => {
      const base = prev[id] || { pickup: false, monthly: false };
      return { ...prev, [id]: { ...base, [key]: val } };
    });

  const accept = async (job: any) => {
    const a = accepts[job.id];
    if (!a?.pickup || !a?.monthly) return;
    setBusyId(job.id);
    try {
      await api.post(`/storage/driver/${job.id}/accept`, { acceptPickup: true, acceptMonthly: true });
      await load();
      Alert.alert('Claimed', 'You accepted both rates. Head to the pickup, then confirm the drop-off with photos + a video.');
    } catch (e: any) {
      Alert.alert('Could not claim', e?.response?.data?.error || 'This job may have just been taken.');
      await load();
    } finally { setBusyId(null); }
  };

  if (loading) {
    return <View style={styles.center}><ActivityIndicator color={RD.maroon} /></View>;
  }

  return (
    <ScrollView style={styles.screen} contentContainerStyle={{ padding: 16, paddingBottom: 48 }}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); load(); }} tintColor={RD.maroon} />}>
      <Text style={styles.h1}>Store to Sell</Text>
      <Text style={styles.sub}>Pick up and store items. You are paid once for the pickup, and every month it stays in storage with you.</Text>

      <Text style={styles.section}>Available to store</Text>
      {available.length === 0
        ? <Text style={styles.empty}>No storage jobs available right now.</Text>
        : available.map(job => {
          const a = accepts[job.id] || { pickup: false, monthly: false };
          const isUnit = job.storageMethod === 'storage_unit';
          return (
            <View key={job.id} style={styles.card}>
              <View style={styles.banner}><Ionicons name="cube" size={14} color="#fff" /><Text style={styles.bannerTxt}>STORAGE REQUIRED — ACCEPT PICKUP RATE AND MONTHLY RATE</Text></View>
              <Text style={styles.title}>{job.productTitle}</Text>
              <Photos images={job.productImages} onOpen={(imgs, i) => setLightbox({ imgs, i })} />
              <Row icon="location-outline" text={job.pickupAddress || 'Pickup address provided after claim'} />
              {!!(job.pickupDate || job.pickupTime) && <Row icon="time-outline" text={`${job.pickupDate || ''} ${job.pickupTime || ''}`.trim()} />}
              {!!job.dimensions && <Row icon="resize-outline" text={`${job.dimensions}${job.cubicFeet ? ` · ${job.cubicFeet} cu ft` : ''}`} />}
              {isUnit && !!job.storageUnitAddress && <Row icon="business-outline" text={`Drop at: ${job.storageUnitLabel || 'unit'} (${job.storageUnitAddress})`} />}
              {isUnit && <Row icon="information-circle-outline" text="Delivered to a storage unit (not stored at your home)." />}

              <View style={styles.rates}>
                <RateRow label="Pickup pay (one time)" value={fmt(job.pickupPayoutCents)} on={a.pickup} onToggle={(v) => toggle(job.id, 'pickup', v)} />
                <RateRow label={`Monthly pay (every month${isUnit ? ', storage unit' : ' it stays with you'})`} value={fmt(job.monthlyCostCents)} on={a.monthly} onToggle={(v) => toggle(job.id, 'monthly', v)} />
              </View>

              <Button title="Accept both rates and claim" variant="maroon" size="lg" fullWidth
                icon="checkmark-circle"
                disabled={!a.pickup || !a.monthly || busyId === job.id}
                loading={busyId === job.id}
                onPress={() => accept(job)} />
            </View>
          );
        })}

      <Text style={styles.section}>My storage jobs</Text>
      {mine.length === 0
        ? <Text style={styles.empty}>None yet.</Text>
        : mine.map(job => {
          const isUnit = job.storageMethod === 'storage_unit';
          const dropTarget = isUnit && job.storageUnitAddress ? `${job.storageUnitLabel || 'unit'} (${job.storageUnitAddress})` : 'your storage';
          return (
            <View key={job.id} style={styles.card}>
              <Text style={styles.title}>{job.productTitle}</Text>
              <Photos images={job.productImages} onOpen={(imgs, i) => setLightbox({ imgs, i })} />
              <Row icon="location-outline" text={job.pickupAddress || ''} />
              {!!job.sellerName && <Row icon="person-outline" text={job.sellerName + (job.sellerPhone ? ` · ${job.sellerPhone}` : '')} />}
              <Row icon="wallet-outline" text={`Pickup ${fmt(job.pickupPayoutCents)} · Monthly ${fmt(job.monthlyCostCents)}`} />
              {job.status === 'CLAIMED' ? (
                <>
                  <Row icon="cube-outline" text={`After pickup, drop at ${dropTarget} and submit 3 photos + a video.`} />
                  <Button title="Confirm drop-off (3 photos + video)" variant="primary" size="lg" fullWidth icon="camera"
                    onPress={() => navigation.navigate('StorageDropOff', { id: job.id, productTitle: job.productTitle })} />
                </>
              ) : (
                <View style={styles.inStorage}><Ionicons name="checkmark-circle" size={16} color={RD.money} /><Text style={styles.inStorageTxt}>In storage{job.pickedUpAt ? ` since ${new Date(job.pickedUpAt).toLocaleDateString()}` : ''}</Text></View>
              )}
            </View>
          );
        })}
      <ImageLightbox images={lightbox?.imgs || []} index={lightbox ? lightbox.i : null} onClose={() => setLightbox(null)} />
    </ScrollView>
  );
}

/** What the driver is collecting: tap any photo to see it full size. */
function Photos({ images, onOpen }: { images?: string[]; onOpen: (imgs: string[], i: number) => void }) {
  const list = (images || []).filter(Boolean);
  if (!list.length) return <Row icon="image-outline" text="No photos yet. Call the seller if you need to see it first." />;
  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginVertical: 8 }}>
      {list.map((uri, i) => (
        <TouchableOpacity key={uri + i} activeOpacity={0.85} onPress={() => onOpen(list, i)}>
          <Image source={{ uri }} style={{ width: 96, height: 96, borderRadius: 10, marginRight: 8, backgroundColor: '#F2F2F2' }} resizeMode="cover" />
        </TouchableOpacity>
      ))}
    </ScrollView>
  );
}

function Row({ icon, text }: { icon: any; text: string }) {
  if (!text) return null;
  return (
    <View style={styles.row}><Ionicons name={icon} size={15} color={RD.textMuted} /><Text style={styles.rowTxt}>{text}</Text></View>
  );
}

function RateRow({ label, value, on, onToggle }: { label: string; value: string; on: boolean; onToggle: (v: boolean) => void }) {
  return (
    <TouchableOpacity activeOpacity={0.8} onPress={() => onToggle(!on)} style={styles.rateRow}>
      <View style={{ flex: 1 }}>
        <Text style={styles.rateLabel}>{label}</Text>
        <Text style={styles.rateValue}>{value}</Text>
      </View>
      <Switch value={on} onValueChange={onToggle} trackColor={{ true: RD.maroon, false: RD.border }} thumbColor="#fff" />
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: RD.bg },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: RD.bg },
  h1: { fontFamily: fonts.semiBold, fontSize: 24, color: RD.ink },
  sub: { fontFamily: fonts.regular, fontSize: 13, color: RD.textSec, marginTop: 4, marginBottom: 12, lineHeight: 19 },
  section: { fontFamily: fonts.semiBold, fontSize: 13, color: RD.maroon, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 18, marginBottom: 8 },
  empty: { fontFamily: fonts.regular, fontSize: 13, color: RD.textFaint, paddingVertical: 12 },
  card: { backgroundColor: RD.surface, borderRadius: 16, borderWidth: 1, borderColor: RD.border, padding: 14, marginBottom: 12 },
  banner: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: RD.maroon, paddingVertical: 6, paddingHorizontal: 10, borderRadius: 8, marginBottom: 10 },
  bannerTxt: { flex: 1, fontFamily: fonts.semiBold, fontSize: 10.5, color: '#fff', letterSpacing: 0.3 },
  title: { fontFamily: fonts.semiBold, fontSize: 16, color: RD.text, marginBottom: 6 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 7, marginTop: 4 },
  rowTxt: { flex: 1, fontFamily: fonts.regular, fontSize: 13, color: RD.textSec },
  rates: { marginVertical: 10, gap: 8 },
  rateRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: RD.maroonSoft, borderRadius: 12, padding: 12, gap: 10 },
  rateLabel: { fontFamily: fonts.medium, fontSize: 12, color: RD.textSec },
  rateValue: { fontFamily: fonts.semiBold, fontSize: 20, color: RD.maroon, marginTop: 2 },
  inStorage: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 8 },
  inStorageTxt: { fontFamily: fonts.medium, fontSize: 13, color: RD.money },
});
